/* @sravni/react-design-system version 0.2.65 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import endOfMonth from 'date-fns/endOfMonth';
import startOfMonth from 'date-fns/startOfMonth';
import endOfWeek from 'date-fns/endOfWeek';
import startOfWeek from 'date-fns/startOfWeek';
import addDays from 'date-fns/addDays';
import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import areIntervalsOverlapping from 'date-fns/areIntervalsOverlapping';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import parse from 'date-fns/parse';
import { useStyles, safeInvoke } from '@sravni/react-utils';
import '../../Space/index.js';
import { DAYS_OF_WEEK } from '../../DateInput/constants/index.js';
import Week from './Week.js';
import modules_75b426be from '../styles/Calendar.module.less.js';
import Space from '../../Space/src/Space.js';

var Month = function (props) {
    var month = props.month, minDate = props.minDate, maxDate = props.maxDate, rangeLimit = props.rangeLimit, disabledIntervals = props.disabledIntervals, selectionStart = props.selectionStart, selectionEnd = props.selectionEnd, selectionInProgress = props.selectionInProgress, mode = props.mode, className = props.className, onChange = props.onChange, restProps = __rest(props, ["month", "minDate", "maxDate", "rangeLimit", "disabledIntervals", "selectionStart", "selectionEnd", "selectionInProgress", "mode", "className", "onChange"]);
    var _a = __read(React.useState(undefined), 2), highlightedStart = _a[0], setHighlightedStart = _a[1];
    var _b = __read(React.useState(undefined), 2), highlightedEnd = _b[0], setHighlightedEnd = _b[1];
    var cx = useStyles(modules_75b426be);
    var classNames = cx(className, 'month');
    var renderHeader = React.useMemo(function () {
        return (React.createElement(Space, { size: 0, align: "center", justify: "center", className: cx('days-of-week') }, DAYS_OF_WEEK.map(function (d) { return (React.createElement(Space, { key: d, size: 0, align: "center", justify: "center", className: cx('day-of-week') }, d)); })));
    }, []);
    var getMinDate = React.useCallback(function () {
        return selectionStart || minDate;
    }, [minDate, selectionStart]);
    var getMaxDate = React.useCallback(function () {
        return selectionStart && rangeLimit ? addDays(selectionStart, rangeLimit) : maxDate;
    }, [maxDate, selectionStart, rangeLimit]);
    var getDisabledRange = React.useCallback(function (interval) {
        var from = interval.from, to = interval.to;
        if (!disabledIntervals)
            return true;
        for (var i = 0; i < disabledIntervals.length; i++) {
            var _a = disabledIntervals[i], intervalStart = _a.from, intervalEnd = _a.to;
            if (areIntervalsOverlapping({
                start: from,
                end: to,
            }, {
                start: intervalStart,
                end: intervalEnd,
            })) {
                return;
            }
        }
        return true;
    }, [disabledIntervals]);
    var pushUpdate = React.useCallback(function (selectionStart, selectionEnd) {
        var from, to;
        if (selectionStart && selectionEnd) {
            if (isBefore(selectionStart, selectionEnd)) {
                from = selectionStart;
                to = selectionEnd;
            }
            else {
                from = selectionEnd;
                to = selectionStart;
            }
            if (rangeLimit && rangeLimit < differenceInCalendarDays(from, to)) {
                to = addDays(from, rangeLimit);
            }
            safeInvoke(onChange, { from: from, to: to });
        }
    }, [rangeLimit, onChange]);
    var handleChangeSelected = React.useCallback(function (date) {
        var from = selectionStart;
        var to = selectionEnd;
        if (mode === 'range') {
            if (selectionInProgress && selectionStart) {
                var isDisabledWithin = getDisabledRange({
                    from: !isBefore(selectionStart, date) ? selectionStart : date,
                    to: isBefore(selectionStart, date) ? selectionStart : date,
                });
                if (!isDisabledWithin) {
                    from = undefined;
                    to = undefined;
                    pushUpdate(from, to);
                    return;
                }
                to = date;
            }
            else {
                from = date;
                to = date;
            }
            pushUpdate(from, to);
        }
        else {
            from = date;
            to = date;
            pushUpdate(from, to);
        }
    }, [mode, selectionStart, selectionInProgress, selectionEnd, pushUpdate, getDisabledRange]);
    var handleOnDayMouseEnter = React.useCallback(function (event) {
        if (!selectionInProgress || !selectionStart)
            return;
        var day = event.currentTarget.dataset.date;
        var date = parse(String(day), 'yyyy-MM-dd', new Date());
        var isDisabledWithin = getDisabledRange({
            from: !isBefore(selectionStart, date) ? selectionStart : date,
            to: isBefore(selectionStart, date) ? selectionStart : date,
        });
        if (!isDisabledWithin)
            return;
        if (date < selectionStart) {
            setHighlightedStart(date);
            setHighlightedEnd(selectionStart);
        }
        else {
            setHighlightedStart(selectionStart);
            setHighlightedEnd(date);
        }
    }, [selectionStart, selectionInProgress, getDisabledRange]);
    var renderWeeks = React.useMemo(function () {
        var innerMinDate = minDate;
        var innerMaxDate = maxDate;
        var start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
        var end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
        var weeks = [];
        if (selectionInProgress && rangeLimit) {
            innerMinDate = getMinDate();
            innerMaxDate = getMaxDate();
        }
        while (isBefore(start, end) || isSameDay(start, end)) {
            weeks.push(start);
            start = addDays(start, 7);
        }
        return weeks.map(function (week) { return (React.createElement(Week, { key: week.getTime(), date: week, month: month, minDate: innerMinDate, maxDate: innerMaxDate, disabledIntervals: disabledIntervals, mode: mode, selectionStart: selectionStart, selectionEnd: selectionEnd, highlightedStart: selectionInProgress ? highlightedStart : undefined, highlightedEnd: selectionInProgress ? highlightedEnd : undefined, onChange: handleChangeSelected, onMouseEnter: handleOnDayMouseEnter })); });
    }, [
        month,
        selectionStart,
        selectionEnd,
        selectionInProgress,
        highlightedStart,
        highlightedEnd,
        rangeLimit,
        minDate,
        maxDate,
        disabledIntervals,
        mode,
        handleChangeSelected,
        handleOnDayMouseEnter,
        getMaxDate,
        getMinDate,
    ]);
    React.useEffect(function () {
        setHighlightedStart(undefined);
        setHighlightedEnd(undefined);
    }, [selectionStart]);
    return (React.createElement("div", __assign({ className: classNames }, restProps),
        renderHeader,
        renderWeeks));
};
Month.displayName = 'Month';

export default Month;
//# sourceMappingURL=Month.js.map
